import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  CLASS_SETTINGS_UPDATED_EVENT,
  useClassSettings,
} from "../../hooks/useData";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { getSchoolYearLabel, useSchoolYear } from "../../context/SchoolYearContext";
import toast from "react-hot-toast";
import { formatPLN } from "../../lib/finance";
import ChangePasswordPanel from "../ui/ChangePasswordPanel";

export default function SettingsScreen() {
  const { data: settings, loading, refresh: refreshClassSettings } = useClassSettings();
  const { isAdmin } = useAuth();
  const {
    selectedSchoolYear,
    selectedSchoolYearId,
    isSelectedYearActive,
    isSelectedYearDraft,
    isSelectedYearArchived,
    refreshSchoolYears,
  } = useSchoolYear();
  const canWrite = isAdmin && (isSelectedYearActive || isSelectedYearDraft);
  const [form, setForm] = useState({ class_name: "", opening_balance: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm((current) => ({
        ...current,
        class_name: settings.class_name ?? "",
      }));
    }
  }, [settings]);

  useEffect(() => {
    setForm((current) => ({
      ...current,
      opening_balance: selectedSchoolYear?.opening_balance ?? "",
    }));
  }, [selectedSchoolYear]);

  async function handleSave(e) {
    e.preventDefault();

    if (!canWrite || !selectedSchoolYearId) {
      toast.error("Rok tylko do odczytu.");
      return;
    }

    if (!settings?.id) {
      toast.error("Nie znaleziono ustawień klasy.");
      return;
    }

    setSaving(true);

    const { error: settingsError } = await supabase
      .from("class_settings")
      .update({
        class_name: form.class_name,
      })
      .eq("id", settings.id);

    if (settingsError) {
      setSaving(false);
      toast.error("Błąd zapisu: " + settingsError.message);
      return;
    }

    const { error: schoolYearError } = await supabase
      .from("school_years")
      .update({
        opening_balance: parseFloat(form.opening_balance) || 0,
      })
      .eq("id", selectedSchoolYearId)
      .in("status", ["active", "draft"]);

    if (schoolYearError) {
      setSaving(false);
      toast.error("Błąd zapisu: " + schoolYearError.message);
      return;
    }

    const refreshedSettings = await refreshClassSettings();
    window.dispatchEvent(new CustomEvent(
      CLASS_SETTINGS_UPDATED_EVENT,
      { detail: refreshedSettings }
    ));
    await refreshSchoolYears();
    setSaving(false);
    toast.success("Ustawienia zapisane");
  }

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <div className="w-8 h-8 border-2 border-gold-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const schoolYearLabel = getSchoolYearLabel(selectedSchoolYear);

  return (
    <div className="space-y-8 max-w-2xl">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <p className="text-xs text-gold-400 uppercase tracking-widest font-medium mb-1">Konfiguracja</p>
        <h1 className="font-display text-3xl font-semibold text-slate-100">Ustawienia klasy</h1>
        <div className="gold-line mt-2" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="card p-6 grid grid-cols-3 gap-4"
      >
        <div>
          <div className="stat-label mb-1">Klasa</div>
          <div className="font-display text-2xl font-semibold text-slate-100">{settings?.class_name ?? "—"}</div>
        </div>
        <div>
          <div className="stat-label mb-1">Rok szkolny</div>
          <div className="font-display text-2xl font-semibold text-slate-100">
            {schoolYearLabel || "—"}
          </div>
        </div>
        <div>
          <div className="stat-label mb-1">Saldo startowe</div>
          <div className="font-display text-2xl font-semibold text-gold-400">
            {formatPLN(selectedSchoolYear?.opening_balance)}
          </div>
        </div>
      </motion.div>

      {isAdmin && isSelectedYearDraft && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="card p-6 text-center text-sky-700 text-sm"
        >
          Wersja robocza roku szkolnego. Dane mogą edytować wyłącznie administratorzy.
        </motion.div>
      )}

      {isAdmin && isSelectedYearArchived && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="card p-6 text-center text-amber-700 text-sm"
        >
          Rok tylko do odczytu. Edycja ustawień jest wyłączona.
        </motion.div>
      )}

      {isAdmin && (
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="card p-6"
        >
          <h2 className="section-title mb-1">Edytuj ustawienia</h2>
          <div className="gold-line mb-6" />
          <form onSubmit={handleSave} className="space-y-5">
            <div>
              <label className="label">Nazwa klasy</label>
              <input
                className="input"
                value={form.class_name}
                onChange={(e) => setForm({ ...form, class_name: e.target.value })}
                placeholder="np. 3B"
                disabled={!canWrite || saving}
              />
            </div>
            <div>
              <label className="label">Rok szkolny</label>
              <div className="input flex items-center text-slate-300">
                {schoolYearLabel || "—"}
              </div>
            </div>
            <div>
              <label className="label">Saldo startowe (PLN)</label>
              <input
                className="input"
                type="number"
                step="0.01"
                value={form.opening_balance}
                onChange={(e) => setForm({ ...form, opening_balance: e.target.value })}
                placeholder="np. 9670.80"
                disabled={!canWrite || saving}
              />
            </div>
            <button type="submit" className="btn-primary" disabled={saving || !canWrite}>
              {saving ? "Zapisywanie..." : "Zapisz zmiany"}
            </button>
          </form>
        </motion.div>
      )}

      {!isAdmin && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="card p-6 text-center text-slate-500 text-sm"
        >
          Edycja ustawień jest dostępna tylko dla administratora
        </motion.div>
      )}

      <ChangePasswordPanel delay={0.3} />
    </div>
  );
}
